import classNames from 'classnames';
import React, { useState } from 'react';
import { ErrorMessage, Field, Formik, useFormikContext } from 'formik';
import * as yup from 'yup';
import { gqlTypes } from '@/types/gqlTypes';
import SingleTextFieldFormGroup from '@/components/form/SingleTextFieldFormGroup';
import { isOptionalUrl, isValidUSPhoneNumber } from '@/lib/utils';
import styles from './ManageAccount.module.scss';
import { useMutation, useQuery } from '@apollo/client';
import { resellerInfoQuery, updateChipResellerInfoQuery } from './ResellerInfo.graphql';
import { useHistory } from 'react-router-dom';
import { Links } from '@/lib/constants';
import StateSelectField from '@/components/form/StateSelectField';
import { ResellerTaxExemptType, ResellerType } from '@/types/gql-op-types';
import checkmark from '@/assets/images/checkmark.svg';

type ContactInfo = gqlTypes.ChipResellerContactInfoInput;
type ShippingAddress = Omit<ContactInfo, 'zipcode'> & { zip: string };

export interface ResellerInfo {
  name?: string | null;
  taxExemptType?: gqlTypes.ResellerTaxExemptType | null;
  contactInfo?: ContactInfo | null;
  shippingAddress?: ShippingAddress | null;
}

// The address entered during the application process is also used as the shipping address for Nano boxes.
// Our 3PL has a max length of 40 characters for address fields.
const ADDRESS_LINE_MAX_LENGTH = 40;
const ADDRESS_LINE_MAX_LENGTH_ERROR = 'Max 40 characters allowed';

const NAME_REGEX = /^[\p{L}\p{N}]+[\p{L}\p{N} ,.'-]*$/u;
const NAME_ERROR_HELPER = `Name must start with a letter or number and contain only letters, numbers, spaces, or the following characters: ,.'-`;

const addressSchema = yup.object({
  line1: yup.string().required(`Please enter an address`).max(ADDRESS_LINE_MAX_LENGTH),
  line2: yup.string().max(ADDRESS_LINE_MAX_LENGTH, ADDRESS_LINE_MAX_LENGTH_ERROR),
  city: yup.string().nullable().required(`Please enter a city`).max(ADDRESS_LINE_MAX_LENGTH),
  zipcode: yup.string().nullable().required(`Please enter a zip code`),
  primaryPhone: yup
    .string()
    .test('is_valid_phone', `Please enter a valid phone number`, isValidUSPhoneNumber)
    .required(`Please enter a phone number`),
  website: yup.string().test('is_url', 'Please enter a valid website', isOptionalUrl),
});

const shippingAddressSchema = yup.object({
  line1: yup.string().required(`Please enter an address`).max(ADDRESS_LINE_MAX_LENGTH),
  line2: yup.string().max(ADDRESS_LINE_MAX_LENGTH, ADDRESS_LINE_MAX_LENGTH_ERROR),
  city: yup.string().nullable().required(`Please enter a city`).max(ADDRESS_LINE_MAX_LENGTH),
  zip: yup.string().nullable().required(`Please enter a zip code`),
});

const validationSchema = yup.object({
  name: yup.string().required(`Please enter a name`).matches(NAME_REGEX, NAME_ERROR_HELPER),
  contactInfo: addressSchema,
  shippingAddress: shippingAddressSchema,
});

interface MultiSelectOption {
  label: string;
  value: string;
}

interface MultiSelectComponentProps {
  name: string;
  options: MultiSelectOption[];
  setFieldValue: (field: string, value: string[], shouldValidate: boolean) => void;
  allowMultiple?: boolean;
  onChange?: (value: string) => void;
  initialValues?: string[] | null;
}

const MultiSelectComponent: React.FC<MultiSelectComponentProps> = ({
  name,
  options,
  setFieldValue,
  allowMultiple = true,
  onChange = null,
  initialValues = null,
}) => {
  const [selectedOptions, setSelectedOptions] = useState<string[]>(() => initialValues ?? []);

  const handleSelectionChange = (value: string) => {
    let updatedSelectedOptions: string[];

    if (allowMultiple) {
      updatedSelectedOptions = selectedOptions.includes(value)
        ? selectedOptions.filter((item) => item !== value)
        : [...selectedOptions, value];
    } else {
      updatedSelectedOptions = selectedOptions.includes(value) ? [] : [value];
    }

    setSelectedOptions(updatedSelectedOptions);
    setFieldValue(name, updatedSelectedOptions, false);

    if (onChange) {
      onChange(value);
    }
  };

  return (
    <div className={styles.multiSelectContainer}>
      {options.map((option) => (
        <div
          key={option.value}
          className={classNames(styles.multiSelectOption, {
            [styles.multiSelectOptionSelected]: selectedOptions.includes(option.value),
          })}
          onClick={() => handleSelectionChange(option.value)}
        >
          <img
            className={classNames(styles.multiSelectImage, {
              [styles.multiSelectImageSelected]: selectedOptions.includes(option.value),
            })}
            src={checkmark}
            alt="checkmark"
          />
          {option.label}
        </div>
      ))}
    </div>
  );
};

interface FormListenerProps {
  onValuesChanged?(values: ResellerInfo): void;
  onIsValidChanged?(isValid: boolean): void;
}

export interface UpdateResellerInfoInput {
  name?: string | null;
  taxExemptType?: gqlTypes.ResellerTaxExemptType | null;
  contactInfo?: ContactInfo | null;
  shippingAddress?: ShippingAddress | null;
}

interface ResellerInfoFormProps extends FormListenerProps {
  initialValues?: ResellerInfo;
  onSkip?: () => void;
  responseError?: string;
  disabled?: boolean;
}

export default function ManageAccount({
  initialValues,
  onSkip,
  onValuesChanged,
  onIsValidChanged,
  responseError,
  disabled,
}: ResellerInfoFormProps) {
  const history = useHistory();
  const { data, loading } = useQuery(resellerInfoQuery);

  const [mutation, { error, data: resellerUpdateData }] = useMutation<
    gqlTypes.updateChipResellerInfo,
    gqlTypes.updateChipResellerInfoVariables
  >(updateChipResellerInfoQuery);

  if (loading || !data) {
    return <>Loading</>;
  }

  const resellerInfo = data?.currentUser?.chipReseller;
  const isRescue = resellerInfo?.types?.includes(ResellerType.RESCUE);

  const onSubmit = async (input: UpdateResellerInfoInput) => {
    const contactInfo: gqlTypes.ChipResellerContactInfoInput = {};
    const shippingAddress: Partial<gqlTypes.AddressInput> = {};

    if (input.contactInfo?.primaryPhone) {
      contactInfo.primaryPhone = input.contactInfo.primaryPhone;
    }
    if (input.contactInfo?.line1) {
      contactInfo.line1 = input.contactInfo.line1;
    }
    if (input.contactInfo?.line2) {
      contactInfo.line2 = input.contactInfo.line2;
    }
    if (input.contactInfo?.city) {
      contactInfo.city = input.contactInfo.city;
    }
    if (input.contactInfo?.state) {
      contactInfo.state = input.contactInfo.state;
    }
    if (input.contactInfo?.zipcode) {
      contactInfo.zipcode = input.contactInfo.zipcode;
    }
    if (input.contactInfo?.website) {
      contactInfo.website = input.contactInfo.website;
    }

    if (input.shippingAddress?.line1) {
      shippingAddress.line1 = input.shippingAddress.line1;
    }
    if (input.shippingAddress?.line2) {
      shippingAddress.line2 = input.shippingAddress.line2;
    }
    if (input.shippingAddress?.city) {
      shippingAddress.city = input.shippingAddress.city;
    }
    if (input.shippingAddress?.state) {
      shippingAddress.state = input.shippingAddress.state;
    }
    if (input.shippingAddress?.zip) {
      shippingAddress.zip = input.shippingAddress.zip;
    }

    try {
      await mutation({
        variables: {
          input: {
            id: resellerInfo.id,
            name: input.name,
            taxExemptType: input.taxExemptType,
            contactInfo,
            shippingAddress,
          },
        },
      });
    } catch (error) {
      window.alert(error.message);
      return;
    }

    history.push(Links.vetChipList());
  };

  return (
    <div className={`${styles.main} single-column`}>
      <header>
        <h1>Manage Account</h1>
      </header>
      <div className={styles.container}>
        <Formik<ResellerInfo>
          initialValues={
            initialValues ?? {
              name: resellerInfo.name ?? '',
              taxExemptType: resellerInfo.taxExemptType ?? null,
              contactInfo: {
                line1: resellerInfo.contactInfo.line1 ?? '',
                line2: resellerInfo.contactInfo.line2 ?? '',
                city: resellerInfo.contactInfo.city ?? '',
                state: resellerInfo.contactInfo.state ?? '',
                zipcode: resellerInfo.contactInfo.zipcode ?? '',
                primaryPhone: resellerInfo.contactInfo.primaryPhone ?? '',
                website: resellerInfo.contactInfo.website ?? '',
              },
              shippingAddress: {
                line1: resellerInfo.shippingAddress.line1 ?? '',
                line2: resellerInfo.shippingAddress.line2 ?? '',
                city: resellerInfo.shippingAddress.city ?? '',
                state: resellerInfo.shippingAddress.state ?? '',
                zip: resellerInfo.shippingAddress.zip ?? '',
              },
            }
          }
          onSubmit={(values: ResellerInfo) => {
            if (!onSubmit) {
              return;
            }

            onSubmit({
              name: values.name?.trim(),
              taxExemptType: values.taxExemptType || null,
              contactInfo: values.contactInfo,
              shippingAddress: values.shippingAddress,
            });
          }}
          validationSchema={validationSchema}
        >
          {({ handleSubmit, submitForm, values, setFieldValue }) => {
            return (
              <form onSubmit={handleSubmit} className={styles.form} autoComplete="on">
                <div className={styles.formGroup}>
                  <div className={classNames('form-field form-field--labelled')}>
                    <p className={styles.groupTitle}>Establishment</p>
                    <SingleTextFieldFormGroup name="name" placeholder="Name" />
                    <SingleTextFieldFormGroup name="contactInfo.primaryPhone" placeholder="Primary Phone" />
                    <SingleTextFieldFormGroup name="contactInfo.website" placeholder="Website" />
                  </div>
                </div>
                {isRescue && (
                  <div className={styles.formGroup}>
                    <p className={styles.groupTitle}>Organization Type</p>
                    <MultiSelectComponent
                      name="taxExemptType"
                      options={[
                        { label: 'Registered 501c3', value: ResellerTaxExemptType.REGISTERED_501C3 },
                        { label: 'Municipal/Government Run Shelter', value: ResellerTaxExemptType.GOVERNMENT_ENTITY },
                        { label: 'Neither', value: '' },
                      ]}
                      setFieldValue={(field, value) => {
                        // Convert array to single value, treating empty string as null
                        const selectedValue = value.length > 0 ? value[0] : null;
                        setFieldValue(field, selectedValue === '' ? null : selectedValue, false);
                      }}
                      allowMultiple={false}
                      initialValues={[resellerInfo.taxExemptType ?? '']}
                    />
                  </div>
                )}
                <div className={styles.formGroup}>
                  <div>
                    <p className={styles.groupTitle}>Address</p>
                    <SingleTextFieldFormGroup name="contactInfo.line1" placeholder="Line 1" />
                    <SingleTextFieldFormGroup name="contactInfo.line2" placeholder="Line 2" />
                    <SingleTextFieldFormGroup name="contactInfo.city" placeholder="City" />
                    <div className={styles.stateAndZipFields}>
                      <div className={styles.stateField}>
                        <StateSelectField name="contactInfo.state" />
                        <ErrorMessage name="contactInfo.state" component="div" className="form-field__error" />
                      </div>
                      <div className={styles.zipField}>
                        <Field type="text" name="contactInfo.zipcode" placeholder="Zip" autoComplete="postal-code" />
                        <ErrorMessage name="contactInfo.zipcode" component="div" className="form-field__error" />
                      </div>
                    </div>
                  </div>
                </div>
                <div className={styles.formGroup}>
                  <div>
                    <p className={styles.groupTitle}>Shipping Address</p>
                    <SingleTextFieldFormGroup name="shippingAddress.line1" placeholder="Line 1" />
                    <SingleTextFieldFormGroup name="shippingAddress.line2" placeholder="Line 2" />
                    <SingleTextFieldFormGroup name="shippingAddress.city" placeholder="City" />
                    <div className={styles.stateAndZipFields}>
                      <div className={styles.stateField}>
                        <StateSelectField name="shippingAddress.state" />
                        <ErrorMessage name="shippingAddress.state" component="div" className="form-field__error" />
                      </div>
                      <div className={styles.zipField}>
                        <Field type="text" name="shippingAddress.zip" placeholder="Zip" autoComplete="postal-code" />
                        <ErrorMessage name="shippingAddress.zip" component="div" className="form-field__error" />
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  className={`${styles.actionButtonGroup} form-group form-group--action form-group--multistep-action`}
                >
                  {responseError && <div className="form-field__error">{responseError}</div>}

                  <button
                    onClick={(e) => {
                      history.push(Links.vetChipList());
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    onClick={(e) => {
                      e.preventDefault();
                      submitForm();
                    }}
                    disabled={disabled}
                  >
                    Save
                  </button>
                </div>
              </form>
            );
          }}
        </Formik>
      </div>
    </div>
  );
}
